import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Kimberley = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kimberley:About Kimberley Paragraph 1">
          Nestled amidst the spectacular Purcell Mountains in southeastern{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Kimberley is a mountain town exuding natural beauty and outdoor
          allure. Renowned for its year-round recreation, winter invites skiing
          and snowboarding enthusiasts to Kimberley Alpine Resort, while summers
          beckon with hiking, biking, golfing, and water sports on picturesque
          lakes. A rich mining history, exemplified by the Sullivan Mine, adds
          depth to its heritage. The Bavarian-themed Platzl area with charming
          shops and eateries enhances the town’s unique charm. Kimberley’s
          strong community spirit is palpable through local events like the
          Kimberley International Old Time Accordion Championship. As a gateway
          to the Purcell Mountains, Kimberley welcomes visitors with a warm
          embrace, making it a gem among the diverse array of{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$700 - $1,700',
          bed2: '$900 - $1,900',
          bed3: '$1,200 - $2,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.kimberley:Apartment Hunting in Kimberley Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Kimberley
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Apartments for rent Kimberley Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Kimberley
              </LinkText>{' '}
              are a popular choice for individuals, couples, and small families
              seeking a convenient and low-maintenance lifestyle. These rental
              units range from older, character-rich buildings to modern
              complexes, often situated to provide easy access to local
              amenities like shops, restaurants, and the unique attractions of
              Kimberley’s pedestrian-friendly Platzl.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Houses for rent Kimberley Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Kimberley
              </LinkText>{' '}
              are ideal for those who prioritize space and privacy. These homes
              can vary from historic, cozy cottages to newer, spacious family
              homes. Many come with yards or gardens, offering a perfect setting
              for outdoor enjoyment and a deeper connection with Kimberley’s
              natural, scenic environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Townhouses for rent Kimberley Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Kimberley
              </LinkText>{' '}
              offer a blend of communal living and private space. Preferred by
              small families, couples, or professionals, these multi-story units
              often come with their own small yards or patios. Situated in
              friendly neighborhoods, they provide a sense of community while
              still allowing for personal space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Basements for rent Kimberley Paragraph">
              Basements for rent in Kimberley offer an affordable alternative
              for renters. These lower-level apartments, part of larger homes,
              are popular among singles, students, or young professionals
              looking for budget-friendly options. They provide essential living
              space and privacy, albeit with limited natural light.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Condos for rent Kimberley Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Kimberley
              </LinkText>{' '}
              appeal to those looking for a hassle-free lifestyle. Many condos
              are located in areas that capitalize on the town’s beautiful
              mountain views and outdoor recreational opportunities. They’re
              especially popular with retirees, young professionals, or small
              families, often offering amenities like shared green spaces or
              recreational facilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Rooms for rent Kimberley Paragraph">
              <LinkText href={links.room}>Rooms for rent in Kimberley</LinkText>{' '}
              are the most cost-effective option, typically chosen by students,
              seasonal workers, or individuals seeking a flexible living
              arrangement. This option involves sharing common areas like
              kitchens and living rooms with other residents, offering an
              opportunity for social interaction and community living.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Short term rentals Kimberley Paragraph">
              Kimberley, a city in the East Kootenay region of British Columbia,
              offers a range of employment opportunities, particularly in
              tourism, retail, and the service industries, driven by its
              reputation as a recreational destination and its charming
              Alpine-themed town center. While it has some educational
              facilities, this city is not widely recognized for a broad
              spectrum of academic institutions.{' '}
              <LinkText href={links.shortTerm}>
                Kimberley sees a noticeable demand for short-term rentals
              </LinkText>
              , especially during peak tourist seasons like winter for skiing
              and summer for golf and outdoor activities. This pattern of demand
              for short-term rentals, linked closely with tourism rather than
              the presence of major academic institutions, differs from larger
              cities where such rentals are popular throughout the year,
              particularly during the summer and at the beginning of school
              semesters. For many, the{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                flexibility of short-term rentals is an important consideration
              </LinkText>
              , especially for those seeking accommodations aligned with
              seasonal work or vacationing in the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.kimberley:Cheap rentals Kimberley Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Kimberley
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Kimberley, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.kimberley:Living in Kimberley Paragraph">
            Located in the heart of the majestic Canadian Rockies, Kimberley, BC
            is a hidden gem that offers a unique blend of natural beauty and
            small-town charm. Surrounded by towering mountains, lush forests,
            and pristine lakes, this picturesque town is a paradise for outdoor
            enthusiasts. Whether you enjoy hiking, skiing, or mountain biking,
            Kimberley has it all. But it’s not just the stunning scenery that
            makes Kimberley special. The tight-knit community and welcoming
            atmosphere create a sense of belonging and make it easy to forge
            lifelong friendships. With its vibrant arts scene, historical
            landmarks, and local festivals, there’s always something to see and
            do in Kimberley. And when you need a break from the hustle and
            bustle, the tranquil ambiance and unhurried pace of life provide the
            perfect escape. Whether you’re a resident or a visitor, Kimberley is
            a place where you can truly live in harmony with nature and
            experience the magic of small-town living.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.kimberley:Living in Kimberley Paragraph 2">
            If you’re not sure that Kimberley is the right city for you, check
            out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kimberley:Popular Neighbourhoods Kimberley Paragraph">
          Like any city, Kimberley is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Town Center, Ski Hill, Marysville, and Forest Crowne. For more
          info about Kimberley, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.kimberley:Local Landmarks Kimberley Paragraph">
          Kimberley, BC, is a town rich in captivating landmarks that showcase
          its natural beauty and historical significance. One prominent landmark
          is the Kimberley Nature Park, a sprawling wilderness area with
          numerous trails that offer breathtaking views of the surrounding
          mountains and forests. This park is a haven for outdoor enthusiasts,
          providing opportunities for hiking, biking, and wildlife spotting.
          Another notable landmark is the Cominco Gardens, an enchanting
          botanical garden filled with vibrant flowers and well-manicured lawns.
          Visitors can stroll through the garden’s various themed sections,
          including a Japanese garden and a rose garden. The Underground Mining
          Railway is also a must-visit landmark in Kimberley. This unique
          attraction takes visitors on an underground tour of a historic mine,
          providing insights into the town’s mining heritage. Additionally, the
          Platzl is a vibrant pedestrian plaza in downtown Kimberley, known for
          its charming Bavarian-themed architecture and lively atmosphere. It is
          home to various shops, restaurants, and cafes, making it the perfect
          place to soak up the town’s culture and indulge in local delicacies.
          These landmarks make Kimberley, BC, an enchanting destination that
          offers a perfect blend of natural wonders, historical treasures, and
          small-town charm.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Kimberley
